import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { customerAuthApi, CustomerInfo, RegisterRequest, LoginRequest } from '../services/customerAuthApi';

interface CustomerAuthContextType {
  customer: CustomerInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (siteNodeId: string, email: string, password: string) => Promise<void>;
  register: (request: RegisterRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

export const CustomerAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Handle Google OAuth callback (redirect back from Google)
    const urlParams = new URLSearchParams(window.location.search);
    const googleAuth = urlParams.get('google_auth');
    const tokenParam = urlParams.get('token');
    const customerData = urlParams.get('customer');
    const errorParam = urlParams.get('error');

    // Build a clean URL preserving non-OAuth query params (e.g. returnUrl)
    const buildCleanUrl = () => {
      const oauthParams = ['google_auth', 'token', 'customer', 'error'];
      const clean = new URLSearchParams();
      urlParams.forEach((value, key) => {
        if (!oauthParams.includes(key)) clean.set(key, value);
      });
      const qs = clean.toString();
      return `${window.location.pathname}${qs ? `?${qs}` : ''}`;
    };

    if (errorParam) {
      const errorMessages: Record<string, string> = {
        'google_auth_cancelled': 'Logowanie przez Google zostało anulowane',
        'missing_code': 'Brak kodu autoryzacji',
        'missing_state': 'Błąd parametrów autoryzacji',
        'invalid_state': 'Nieprawidłowe parametry autoryzacji',
        'exchange_failed': 'Błąd wymiany kodu autoryzacji',
        'login_failed': 'Błąd logowania',
        'server_error': 'Błąd serwera',
      };
      setError(errorMessages[errorParam] || 'Błąd logowania przez Google');
      window.history.replaceState({}, document.title, buildCleanUrl());
      setIsLoading(false);
      return;
    }

    if (googleAuth === 'success' && tokenParam && customerData) {
      try {
        const raw = JSON.parse(decodeURIComponent(customerData));
        const parsed: CustomerInfo = {
          id: raw.Id || raw.id,
          siteNodeId: raw.SiteNodeId || raw.siteNodeId,
          name: raw.Name || raw.name,
          email: raw.Email || raw.email,
          phone: raw.Phone || raw.phone || '',
        };

        localStorage.setItem('customer_token', tokenParam);
        setCustomer(parsed);
        window.history.replaceState({}, document.title, buildCleanUrl());
      } catch (err) {
        console.error('Error processing Google OAuth callback:', err);
        setError('Błąd przetwarzania danych logowania');
        window.history.replaceState({}, document.title, buildCleanUrl());
      }
      setIsLoading(false);
      return;
    }

    // Normal flow: check for existing token
    const token = localStorage.getItem('customer_token');
    if (token) {
      customerAuthApi
        .getCurrentCustomer(token)
        .then(setCustomer)
        .catch(() => {
          localStorage.removeItem('customer_token');
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (siteNodeId: string, email: string, password: string) => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await customerAuthApi.loginCustomer({ siteNodeId, email, password });
      localStorage.setItem('customer_token', response.token);
      setCustomer(response.customer);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (request: RegisterRequest) => {
    try {
      setError(null);
      setIsLoading(true);
      const response = await customerAuthApi.registerCustomer(request);
      localStorage.setItem('customer_token', response.token);
      setCustomer(response.customer);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('customer_token');
    setCustomer(null);
  };

  const clearError = () => setError(null);

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        isAuthenticated: !!customer,
        isLoading,
        error,
        login,
        register,
        logout,
        clearError,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};
